///
/// EarthBrowser - Privacy Consent Management
///
/// Created: 2025
/// Copyright (c) 2025 Geodata Labs
///
/// Centralized consent management for analytics (PostHog) and error tracking (Sentry)
///
/// Web: both follow the cookie banner / profile privacy toggle and are off
/// until the visitor opts in. Native apps (Capacitor): there is no banner;
/// first-party crash reporting is on by default (declared in the store
/// privacy labels, no ATT prompt needed since nothing is shared for ad
/// tracking) while PostHog stays opt-in through the profile settings.
///

import posthog from 'posthog-js'
import * as Sentry from '@sentry/react'
// platform.ts directly (not '@/native') to keep this leaf module free of
// the redux store that the native auth bridge pulls in
import { isNativeApp } from '@/native/platform'

const COOKIE_CONSENT_KEY = 'cookieConsent'
const PERFORMANCE_CONSENT_KEY = 'performanceCookies'

/** Crash reporting runs with consent on the web and unconditionally in the apps */
export function crashReportingAllowed(): boolean {
	return isNativeApp() || hasPerformanceConsent()
}

// Check if user has given consent for performance/analytics cookies
export function hasPerformanceConsent(): boolean {
	// Check localStorage first (for granular settings)
	const performanceConsent = localStorage.getItem(PERFORMANCE_CONSENT_KEY)
	if (performanceConsent !== null) {
		return performanceConsent === 'true'
	}

	// Fall back to cookie consent
	return document.cookie.includes(`${COOKIE_CONSENT_KEY}=true`)
}

// Check if user has made any consent choice
export function hasConsentChoice(): boolean {
	return localStorage.getItem(PERFORMANCE_CONSENT_KEY) !== null ||
		document.cookie.includes(COOKIE_CONSENT_KEY)
}

// Update consent state and control tracking services
export function updatePerformanceConsent(enabled: boolean): void {
	localStorage.setItem(PERFORMANCE_CONSENT_KEY, enabled ? 'true' : 'false')

	if (enabled) {
		enableTracking()
	} else {
		disableTracking()
	}
}

// Enable all tracking services
export function enableTracking(): void {
	// Enable PostHog
	if (posthog?.has_opted_out_capturing?.()) {
		posthog.opt_in_capturing()
	}

	setSentryEnabled(true)
}

// Disable all tracking services
export function disableTracking(): void {
	// Disable PostHog
	if (typeof posthog !== 'undefined' && !posthog.has_opted_out_capturing?.()) {
		posthog.opt_out_capturing()
	}

	// Native apps keep crash reporting on regardless of the analytics choice
	setSentryEnabled(isNativeApp())
}

function setSentryEnabled(enabled: boolean): void {
	const client = Sentry.getClient()
	if (client) {
		client.getOptions().enabled = enabled
	}
}

// Initialize tracking based on current consent state
export function initializeTrackingConsent(): void {
	if (!hasConsentChoice()) {
		// No choice made yet - disable by default (GDPR compliant)
		disableTracking()
		return
	}

	if (hasPerformanceConsent()) {
		enableTracking()
	} else {
		disableTracking()
	}
}

